import { type Static, Type } from '@sinclair/typebox';
import { FilterSchema, PaginatedSchema, SavingPercentageRangeSchema, SwapFiltersSchema } from './common.js';
import { PaginatedSwapsSchema } from './product.js';
import { TaxonDTOSchema } from './taxon.js';

// =====================================================================================================================
// Trigger Batch
// =====================================================================================================================
export const ExcelUploadIDsAndPricesSchema = Type.Object({
  supplierID: Type.String(),
  productCode: Type.String(),
  newPrice: Type.Union([Type.Number(), Type.String()]),
});
export type ExcelUploadIDsAndPrices = Static<typeof ExcelUploadIDsAndPricesSchema>;

export const InputUploadsSchema = Type.Object({
  excelUploadIDsAndPrices: Type.Optional(Type.Array(ExcelUploadIDsAndPricesSchema)),
  fileName: Type.Optional(Type.String()),
});

export type InputUploads = Static<typeof InputUploadsSchema>;

export const TriggerBatchBodySchema = Type.Object({
  productFilters: FilterSchema,
  swapFilters: FilterSchema,
  inputUploads: Type.Optional(InputUploadsSchema),
});
export type TriggerBatchBody = Static<typeof TriggerBatchBodySchema>;

// =====================================================================================================================

const BatchInputCatalogSchema = Type.Object({
  catalogName: Type.String(),
  organizationKey: Type.String(),
  taxonomy: Type.String(),
});
export type BatchInputCatalogDTO = Static<typeof BatchInputCatalogSchema>;

export const BatchMetadataSchema = Type.Object({
  createdAt: Type.Number(),
  forRefreshDate: Type.Number(),
  requester: Type.Object({
    email: Type.String(),
    name: Type.String(),
  }),
  status: Type.Union([
    Type.Literal('PENDING'),
    Type.Literal('PROCESSING'),
    Type.Literal('COMPLETED'),
    Type.Literal('FAILED'),
    Type.Literal('OUTDATED'),
  ]),
});
export type BatchMetadataDTO = Static<typeof BatchMetadataSchema>;

export const BatchInputDTOSchema = Type.Object({
  catalog: BatchInputCatalogSchema,
  productFilters: Type.Intersect([
    Type.Pick(FilterSchema, ['criterias', 'dateRange']),
    Type.Object({
      category: Type.Pick(TaxonDTOSchema, ['_id', '_key', 'name']),
    }),
  ]),
  savingsPercentageRange: Type.Optional(SavingPercentageRangeSchema),
  similarityThreshold: Type.Number(),
  swapFilters: Type.Intersect([
    Type.Pick(FilterSchema, ['criterias', 'dateRange']),
    Type.Object({
      category: Type.Pick(TaxonDTOSchema, ['_id', '_key', 'name']),
    }),
  ]),
  swapTypeAcceptance: Type.Literal('ALL_FEASIBLE'),
  inputUploadID: Type.Optional(Type.String()),
  inputUploads: Type.Optional(InputUploadsSchema),
});
export type BatchInputDTO = Static<typeof BatchInputDTOSchema>;

export const BatchDTOSchema = Type.Recursive(T =>
  Type.Object({
    _key: Type.Optional(Type.String()),
    input: Type.Optional(BatchInputDTOSchema),
    inputHash: Type.Optional(Type.String()),
    metadata: Type.Optional(BatchMetadataSchema),
    olderRequests: Type.Optional(Type.Array(T)),
    output: Type.Optional(PaginatedSwapsSchema),
  }),
);
export type BatchDTO = Static<typeof BatchDTOSchema>;

// =====================================================================================================================
// Retrieve Batch
// =====================================================================================================================

export const RetrieveBatchParamsSchema = Type.Object({
  batchKey: Type.String(),
});
export type RetrieveBatchParams = Static<typeof RetrieveBatchParamsSchema>;

export const RetrieveBatchQuerySchema = Type.Object({
  fields: Type.Optional(Type.String()),
});
export type RetrieveBatchQuery = Static<typeof RetrieveBatchQuerySchema>;

// =====================================================================================================================
// Retrieve Batch Output
// =====================================================================================================================

export const RetrieveBatchOutputParamsSchema = Type.Object({
  batchRequestKey: Type.String(),
});
export type RetrieveBatchOutputParams = Static<typeof RetrieveBatchOutputParamsSchema>;

export const RetrieveBatchOutputBodySchema = Type.Object({
  offset: Type.Optional(Type.Number()),
  limit: Type.Optional(Type.Number()),
  fields: Type.Object({
    products: Type.String(),
    swaps: Type.String(),
  }),
  restrictTo: Type.Optional(Type.Array(Type.Object({ productKey: Type.String(), swapKey: Type.String() }))),
  swapFilters: Type.Omit(SwapFiltersSchema, ['unit']),
});
export type RetrieveBatchOutputBody = Static<typeof RetrieveBatchOutputBodySchema>;

// =====================================================================================================================
// Retrieve Batch By Input
// =====================================================================================================================
export const RetrieveBatchByInputBodySchema = Type.Object({
  input: Type.Object({
    productFilters: FilterSchema,
    swapFilters: FilterSchema,
  }),
  fields: Type.Optional(Type.String()),
});
export type RetrieveBatchByInputBody = Static<typeof RetrieveBatchByInputBodySchema>;

// =====================================================================================================================
// Retrieve Batch Status with count for a user
// =====================================================================================================================

export const RetrieveBatchStatusCountForUserParamsSchema = Type.Object({ userEmail: Type.String() });
export type RetrieveBatchStatusCountForUserParams = Static<typeof RetrieveBatchStatusCountForUserParamsSchema>;

export const RetrieveBatchStatusCountForUserResponseSchema = Type.Array(
  Type.Object({
    status: BatchMetadataSchema.properties.status,
    count: Type.Number(),
  }),
);
export type RetrieveBatchStatusCountForUserResponse = Static<typeof RetrieveBatchStatusCountForUserResponseSchema>;

// =====================================================================================================================
// List Batch
// =====================================================================================================================

export const ListBatchBodySchema = Type.Object({
  userEmail: Type.Optional(Type.String()),
  fields: Type.Optional(Type.String()),
  limit: Type.Optional(Type.Number()),
  offset: Type.Optional(Type.Number()),
  inputHash: Type.Optional(Type.String()),
});
export type ListBatchBody = Static<typeof ListBatchBodySchema>;

export const ListBatchResponseSchema = PaginatedSchema(BatchDTOSchema);
export type ListBatchResponse = Static<typeof ListBatchResponseSchema>;

// =====================================================================================================================
// Delete Batch
// =====================================================================================================================

export const DeleteBatchRequestParamsSchema = Type.Object({
  batchKey: Type.String(),
});
export type DeleteBatchRequestParams = Static<typeof DeleteBatchRequestParamsSchema>;

// =====================================================================================================================
// Check async
// =====================================================================================================================

export const CheckAsyncBodySchema = Type.Object({
  leftFilters: Type.Object({
    productFilters: FilterSchema,
    swapFilters: FilterSchema,
  }),
  hasInputUpload: Type.Optional(Type.Boolean()),
});
export type CheckAsyncBody = Static<typeof CheckAsyncBodySchema>;
